import { useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@sequencehq/api'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { useNotifications } from '@sequencehq/core-components'
import { downloadBlob } from '@sequencehq/utils'
import SpinnerContainer from 'components/Loading/SpinnerContainer'

const JOURNAL_REPORTS_PATH = '/revenue-recognition/journal-reports'
const DEFAULT_FILENAME = 'analysis-report.xlsx'

export const getFilenameFromUrl = (url: string): string => {
  try {
    const filename = new URL(url).pathname.split('/').pop()
    return filename || DEFAULT_FILENAME
  } catch {
    return DEFAULT_FILENAME
  }
}

export const AnalysisReportDownload = () => {
  const [searchParams] = useSearchParams()
  const signature = searchParams.get('signature')
  const navigate = useNavigate()
  const { displayNotification } = useNotifications()

  const { data, isError, isSuccess } = useQuery(
    dashboardv99990101Client.getAnalysisReportSignedDownload,
    { signature: signature ?? '' },
    { enabled: !!signature }
  )

  useEffect(() => {
    if (isSuccess && data?.url) {
      // Fetch the signed URL and stream it to a download so the user stays
      // in-app with a sensible filename, matching the journal-report and
      // deferred-revenue download routes.
      ;(async () => {
        try {
          const response = await fetch(data.url)
          if (!response.ok) {
            throw new Error('Download failed')
          }

          const blob = await response.blob()
          downloadBlob(getFilenameFromUrl(data.url))(blob)

          displayNotification('Downloaded successfully', { type: 'success' })
        } catch {
          displayNotification('Failed to download report', { type: 'error' })
        } finally {
          navigate(JOURNAL_REPORTS_PATH)
        }
      })()
    } else if (isError || !signature || (isSuccess && !data?.url)) {
      displayNotification('This download link has expired or is invalid', {
        type: 'error'
      })
      navigate(JOURNAL_REPORTS_PATH)
    }
  }, [signature, isSuccess, isError, data, displayNotification, navigate])

  return <SpinnerContainer />
}
